// src/components/BoardPack/pages/LargeTable.jsx
import React, { useState, useEffect, useRef } from "react";
import mawgifLogo from "@/assets/img/images.png";
import ErrorState from "@/components/common/ErrorStateComponent";
import { apiCall } from "@/api/apiCall";
import { useQueue } from "@/context/QueueContext";
import LoadingComponent from "@/components/common/LoadingComponent";
import HeadingComponent from "../components/shared/heading";
import { processParams } from "../utils/paramsHelper";
import { useNavigate } from "react-router-dom";
import AiCfoSection from "../components/shared/AiCfoSection";

// ─────────────────────────────────────────────────────────────────
// THEME
// ─────────────────────────────────────────────────────────────────
const theme = {
  headerBText: "#000000",
  headerCText: "#000000",

  labelSectionHeaderBg: "#FFFFFF",
  labelTotalBg: "#FFFF00",
  cellWhite: "#FFFFFF",
  cellGreen: "#E2EFDA",
  red: "#FF0000",
  black: "#000000",
  bDark: "#000000",
  bMedium: "#A0A0A0",
  bLight: "#C8C8C8",

  cellGray:      "#D9D9DA",
  cellGrayLight:      "#dbe0f0",
  labelDefaultBg:"#E2EFDB",
  labeluniqueBg: "#DEEBF4",
  cellYellow:    "#FFFF02",

  // ── Column-group colours (Excel-style bands) ──────────────────────
  colActual:         "#FFFFCC",  // light yellow  – Actual period
  colBudget:         "#E1E1E1",  // light gray    – Budget period
  colOutturnVBudget: "#FCE4D6",  // light peach   – Outturn V Budget group
  colFYOutturn:      "#FFE699",  // light amber   – FY Outturn cols (12 & 16)
  colPriorYear:      "#E2EFDA",  // light green   – 2025 Actual & 2026 V 2025
};

// ─────────────────────────────────────────────────────────────────
// Row / Col type sets
// ─────────────────────────────────────────────────────────────────
const TOTAL_LABELS = ["Total Revenue", "EBITDA", "OPERATING PROFIT/(LOSS)"];
const SECTION_HEADERS = [
  "Revenue",
  // "Total Rent",
  // "Zakat, Exceptional & CG",
  // "Total Depreciation & Amortization",
];
const PERCENT_LABELS = [
  "Total Rent",
  "Total Staff Cost - Direct",
  "Total Other Direct",
  "Total Depreciation & Amortization",
  "Zakat, Exceptional & CG"
];
const GRAY_CELL_ROWS = [
  "Total Revenue",
  "Total Rent",
  "Total Staff Cost - Direct",
  "Total Other Direct",
  "OPERATING PROFIT/(LOSS)",
  "Total Depreciation & Amortization",
  "Zakat, Exceptional & CG",
];
const BOLD_LABELS = ["Total Revenue", "EBITDA","EBITDA Margin", "OPERATING PROFIT/(LOSS)","NET PROFIT MARGIN","EBITDA MARGIN"];
const Unique_LABELS = ["Rent V Revenue %", "Staff Cost-Direct %", "EBITDA Margin","NET PROFIT MARGIN"];

// ─────────────────────────────────────────────────────────────────
const THICK_LEFT_COLS = new Set([0, 12,13,16,17]);

const getColLeftBorder = (ci) =>
  THICK_LEFT_COLS.has(ci) ? `1px solid ${theme.bDark}` : `1px solid ${theme.bLight}`;
const getColRightBorder = (ci, total) =>
  ci === total - 1 ? `1px solid ${theme.bDark}` : ci === 11 || ci === 12 || ci === 15 || ci === 16 ? `1px solid ${theme.bDark}` : `1px solid ${theme.bLight}`;

const THICK_TOP_ROWS = new Set(["Revenue"]);
const THICK_BOTTOM_ROWS = new Set([
  "Total Revenue",
  "EBITDA",
  "Zakat, Exceptional & CG",
  "NET PROFIT MARGIN",
]);
const MEDIUM_BOTTOM_ROWS = new Set([
  "Total Other Direct",
  "EBITDA Margin",
  "Total Depreciation & Amortization",
]);

const getRowTopBorder = (label) =>
  THICK_TOP_ROWS.has(label) ? `1px solid ${theme.bDark}` : `1px solid ${theme.bLight}`;
const getRowBottomBorder = (label) =>
  THICK_BOTTOM_ROWS.has(label)
    ? `1px solid ${theme.bDark}`
    : MEDIUM_BOTTOM_ROWS.has(label)
      ? `1px solid ${theme.bMedium}`
      : `1px solid ${theme.bLight}`;


// ─────────────────────────────────────────────────────────────────
// Transform
// ─────────────────────────────────────────────────────────────────
export function transformApiResponse(response) {
  const { data } = response;
  const numCols = Math.min(
    data.headerYears?.length || 0,
    data.headerMonths?.length || 0,
    data.headerThird?.length || 0
  );
  const columns = Array.from({ length: numCols }, (_, i) => ({
    year: data.headerYears[i] || "",
    period: data.headerMonths[i] || "",
    type: data.headerThird[i] || "",
  }));
  const rows = data.rows.map((item) => ({
    label: item.label || "",
    values: Array.isArray(item.values) ? item.values.map((v) => (v === "" ? "-" : v)) : [],
    isPercent: item.isPercent || false,
    isTotal: TOTAL_LABELS.includes(item.label),
    isSectionHeader: SECTION_HEADERS.includes(item.label),
  }));
  return { columns, rows };
}

// ─────────────────────────────────────────────────────────────────
// TableComponent
// ─────────────────────────────────────────────────────────────────
export function TableComponent({ title, columns, rows, contract }) {
  const N = columns.length; // 21 data cols

  const labelBg = (label) => {
    if (TOTAL_LABELS.includes(label)) return theme.cellYellow;
    if (SECTION_HEADERS.includes(label)) return theme.labelSectionHeaderBg;
    if (PERCENT_LABELS.includes(label)) return theme.cellGray;
    if (Unique_LABELS.includes(label)) return theme.labeluniqueBg;
    return theme.labelDefaultBg;
  };

  // Returns the column-group background colour based on year/period metadata
  const getColGroupBg = (ci) => {
    const col = columns[ci];
    if (!col) return theme.cellGray;
    const { year, period } = col;
    if (ci === 12 || ci === 16)            return theme.colFYOutturn;
    if (period === "Actual")               return theme.colActual;
    if (period === "Budget")               return theme.cellGray; // colBudget
    if (year === "Outturn V Budget")       return theme.colOutturnVBudget;
    if (year === "2025" || year === "2026 V 2025") return theme.colPriorYear;
    return theme.cellGray;
  };

  const cellBg = (label, ci) => {
    // FY Outturn column – bright yellow on key total rows
    if (
      ci === 12 &&
      (label === "Total Revenue" || label === "EBITDA" || label === "OPERATING PROFIT/(LOSS)")
    ) {
      return theme.cellYellow;
    }
    // Special row overrides
        // if (label === "EBITDA")                return theme.cellGreen;
    if (label === "EBITDA")                return "#E1E1E1";
    // if (label === "EBITDA")                return theme.cellWhite;
    if (GRAY_CELL_ROWS.includes(label))    return theme.cellGray;
    if (SECTION_HEADERS.includes(label))   return theme.cellWhite;
    if (PERCENT_LABELS.includes(label))    return theme.cellGray;
    // All other data cells → uniform light yellow
    return theme.colActual;
  };

  const subLabelStyle = (bg, color, isLastHdrRow) => ({
    padding: "2px 5px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: "9.6px",
    backgroundColor: bg,
    color,
    borderLeft: `1px solid ${theme.bDark}`,
    borderRight: `1px solid ${theme.bDark}`,
    borderTop: "none",
    borderBottom: isLastHdrRow ? `1px solid ${theme.bDark}` : `1px solid ${theme.bLight}`,
    whiteSpace: "nowrap",
  });

  const hdrDataStyle = (ci, isLastHdrRow) => {
    // Cols after Budget-Dec (ci >= 12) → navy blue header with white text + white internal borders
    if (ci >= 12) {
      return {
        padding: "2px 2px",
        textAlign: "center",
        fontWeight: 600,
        fontSize: "9.6px",
        backgroundColor: ci === 12 || ci === 16  ? theme.cellYellow: "#002060",
        color: ci === 12 || ci === 16 ? theme.black: "#FFFFFF",
        // color: "#FFFFFF",
        borderLeft: getColLeftBorder(ci),
        borderRight: ci === N - 1 ? `2px solid ${theme.bDark}` : `1px solid ${theme.bDark}`,
        borderTop: "none",
        borderBottom: isLastHdrRow
          ? `2px solid ${theme.bDark}`
          : `1px solid ${theme.bDark}`,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };
    }

    // Cols 0-11: column-group colour
    return {
      padding: "2px 2px",
      textAlign: "center",
      fontWeight: 600,
      fontSize: "9.6px",
      backgroundColor: getColGroupBg(ci),
      color: theme.headerBText,
      borderLeft: getColLeftBorder(ci),
      borderRight: getColRightBorder(ci, N),
      borderBottom: isLastHdrRow ? `1px solid ${theme.bDark}` : `1px solid ${theme.bLight}`,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
  };

  return (
    <div style={{ width: "100%" }}>
      <table
        style={{
          width: "100%",
          // tableLayout: "fixed",
          fontSize: "0.528rem",
          borderCollapse: "collapse",
          // minWidth: "1900px",
        }}
        className="mt-2"
      >
        <colgroup>
          <col style={{ width: "144px" }} /> col 0: logo
          <col style={{ width: "56px" }} /> {/* col 1: row-label */}
          {columns.map((_, i) => (
            <col key={i} style={{ width: "61px" }} />
          ))}{" "}
          {/* col 2+: data */}
        </colgroup>

        <thead>
          <tr className="bg-table-header-column-navyBlue">
            <td
              // rowSpan={4}
              style={{
                textAlign: "center",
                verticalAlign: "middle",
                padding: "1px",
                backgroundColor: "#FFFFFF",
                borderRight: `2px solid ${theme.bDark}`,
                borderTop: `2px solid ${theme.bDark}`,
                borderLeft: `2px solid ${theme.bDark}`,
              }}
            >
              <img
                src={mawgifLogo}
                alt="Logo"
                style={{ width: "20%", objectFit: "contain", display: "block", margin: "0 auto" }}
              />
            </td>
            {/* <td style={{ borderTop: `2px solid ${theme.bDark}` }}></td> */}
            <td
              colSpan={N}
              style={{
                color: "white",
                textAlign: "center",
                fontWeight: 700,
                fontSize: "16px",
                padding: "4px 3px",
                borderRight: `2px solid ${theme.bDark}`,
                borderBottom: `1px solid ${theme.bDark}`,
                borderTop: `2px solid ${theme.bDark}`,
              }}
            >
              {title || "Profit and Loss - Current Month / YTD / Full Year"}
            </td>
          </tr>

          <tr>
            {/* <th style={subLabelStyle("#000000", "#FFFFFF", false)}>{contract || "ALL COMPANIES"}</th> */}
            <th style={subLabelStyle("#000000", "#FFFFFF", false)}> {contract?.split(" - ")[0]}</th>
            {columns.map((col, idx) => (
              <th
                key={`yr-${idx}`}
                style={hdrDataStyle(idx, false)}
              >
                {col.year}
              </th>
            ))}
          </tr>

          <tr>
            <th style={subLabelStyle(theme.cellGray, theme.headerBText, false)}>
              Total Cost Center
            </th>
            {columns.map((col, idx) => (
              <th
                key={`mo-${idx}`}
                style={hdrDataStyle(idx, false)}
              >
                {col.period}
              </th>
            ))}
          </tr>

          <tr>
            <th style={subLabelStyle(theme.cellGray, theme.headerBText, true)}>All Currency</th>
            {columns.map((col, idx) => (
              <th
                key={`th-${idx}`}
                style={hdrDataStyle(idx, true)}
              >
                {col.type}
              </th>
            ))}
          </tr>
        </thead>

        {/* ══════════ TBODY ══════════ */}
        <tbody>
          {rows.map((row, ri) => {
            const bTop = getRowTopBorder(row.label);
            const bBottom = getRowBottomBorder(row.label);
            const lbg = labelBg(row.label);
            const bold = BOLD_LABELS.includes(row.label);
            const isEmpty = row.values.length === 0;

            return (
              <tr key={ri}>
                {/* Row-label cell */}
                <td
                  style={{
                    paddingLeft: "6px",
                    paddingRight: "3px",
                    paddingTop: "1px",
                    paddingBottom: "1px",
                    borderLeft: `1px solid ${theme.bDark}`,
                    borderRight: `1px solid ${theme.bDark}`,
                    borderTop: bTop,
                    borderBottom: bBottom,
                    backgroundColor: lbg,
                    fontWeight: bold ? 700 : 400,
                    fontSize: "9.6px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: theme.black,
                  }}
                >
                  {row.label}
                </td>

                {/* Data cells */}
                {isEmpty ? (
                  <td
                    colSpan={N}
                    style={{
                      backgroundColor: theme.cellWhite,
                      borderRight: `2px solid ${theme.bDark}`,
                      borderTop: bTop,
                      borderBottom: bBottom,
                    }}
                  />
                ) : (
                  row.values.map((val, ci) => {
                    const bg = cellBg(row.label, ci);
                    const neg = typeof val === "string" && val.startsWith("(") && val !== "-";
                    return (
                      <td
                        key={ci}
                        style={{
                          textAlign: "right",
                          // padding: "1px",
                          borderLeft: getColLeftBorder(ci),
                          borderRight: getColRightBorder(ci, N),
                          borderTop: bTop,
                          borderBottom: bBottom,
                          backgroundColor: bg,
                          color: neg ? theme.red : theme.black,
                          fontWeight: bold && val !== "-" ? 700 : row.isPercent ? 600 : 400,
                          fontSize: "0.528rem",
                          whiteSpace: "nowrap",
                        }}
                        className=" font-medium min-w-[48px] max-w-[48px]"
                      >
                        {val}
                      </td>
                    );
                  })
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main LargeTable Component
// ─────────────────────────────────────────────────────────────────
function LargeTable({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isExporting = false,
  cfoComment = "",
  onCfoCommentChange,
  isRefresh = false 
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });

  const hasComment = cfoComment.trim() !== "";
  const hasFetchedRef = useRef(false);

  const { enqueue } = useQueue();
  const navigate = useNavigate();

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const is_cfo = page?.params?.is_cfo ? true : false;
  const title = page?.title;
  const subtitle = page?.subtitle;
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSummary(null);
      const apiParams = processParams(page?.params || {}, { year, month, contract });
      const response = await enqueue(() => apiCall("GET", "/board-pack/page-one", apiParams,navigate,false,false,isRefresh));
      if (!response?.success) throw new Error(response?.message || "API error");
      setSummary({ lines: response.lines || [] });
      const { columns, rows } = transformApiResponse(response);
      setTableData({ columns, rows });
    } catch (err) {
      console.error("Profit & Loss fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginTop: "4px",
      }}
    >
      <div className="flex flex-col mt-1">
        {title && <HeadingComponent variant={"md"}>{title}</HeadingComponent>}
        {subtitle && <HeadingComponent variant={"sm"}>{subtitle}</HeadingComponent>}
      </div>
      <div className={`px-[16px]`}>
        <TableComponent
          // title={page?.title || "Profit and Loss - Current Month / YTD / Full Year"}
          title={"Profit and Loss - Current Month / YTD / Full Year"}
          columns={tableData.columns}
          rows={tableData.rows}
          contract={page?.subtitle}
        />
      </div>
      <AiCfoSection
  isCfo={is_cfo}
  summary={summary}
  cfoComment={cfoComment}
  onCfoCommentChange={onCfoCommentChange}
  isExporting={isExporting}
  compact={true}
/>
    </div>
  );
}

export default LargeTable;

